export const DRAFT_STORAGE_KEY = 'contractornerd.equipment-draft.v1';
const API_ENDPOINT = process.env.REACT_APP_EQUIPMENT_DRAFT_ENDPOINT
  || `${(process.env.REACT_APP_API_ENDPOINT || 'https://app.contractornerd.com/api/form-data').replace(/\/$/, '')}/equipment`;

export async function requestEquipmentDraft(input, { keepalive = false } = {}) {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 15000);
  try {
    const body = JSON.stringify(input);
    const response = await fetch(API_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      signal: controller.signal,
      // Browsers cap keepalive requests at 64 KiB. Normal saves support larger
      // schedules; the local pending snapshot also survives navigation.
      keepalive: keepalive && new Blob([body]).size < 60000,
    });
    const result = await response.json();
    if (!response.ok || !result.success) {
      const error = new Error(result.error || 'Could not save your progress. Please retry.');
      error.status = response.status;
      throw error;
    }
    return result.draft;
  } finally {
    clearTimeout(timeout);
  }
}

export async function requestEquipmentAttachments(input) {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 30000);
  try {
    const response = await fetch(`${API_ENDPOINT}/attachments`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input), signal: controller.signal,
    });
    const result = await response.json();
    if (!response.ok || !result.success) {
      const error = new Error(result.error || 'Could not save the attachment. Please retry.');
      error.status = response.status;
      throw error;
    }
    return result;
  } finally { clearTimeout(timeout); }
}

const snapshotOf = (draft) => ({
  formData: draft.data.formData,
  equipment: draft.data.equipment,
  totalEquipmentValue: draft.data.totalEquipmentValue,
  currentStep: draft.data.progress.currentStep,
});

// One browser draft owns one Admin row. Pending operations are retained with
// their original saveId/revision so a lost response cannot create duplicates.
export function createEquipmentDraftClient({ storage, request = requestEquipmentDraft, attachmentRequest = requestEquipmentAttachments, onStatus = () => {}, delay = 800 } = {}) {
  let session;
  try { session = JSON.parse(storage?.getItem(DRAFT_STORAGE_KEY) || 'null'); } catch { /* storage may be disabled */ }
  if (!session || !/^[a-f0-9-]{36}$/i.test(session.id || '') || !/^[a-f0-9]{64}$/.test(session.token || '')
    || !Number.isSafeInteger(session.revision) || session.revision < 0) {
    const bytes = new Uint8Array(32);
    crypto.getRandomValues(bytes);
    session = { id: crypto.randomUUID(), token: Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join(''), revision: 0, snapshot: null, pending: null, dirty: false };
  }
  let timer;
  let inFlight;
  let restored;
  let lastResult;
  let attachmentVersion = 0;
  let resumable = false;
  const persist = () => {
    try {
      if (storage) {
        storage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(session));
        resumable = true;
      }
    } catch { resumable = false; /* server saving still works */ }
  };
  persist();
  const send = (operation, options) => request({ draftId: session.id, draftToken: session.token, ...operation }, options);
  const notifyError = (error) => onStatus('error', error.status === 409 || error.status === 403
    ? error.message : 'Could not save progress. Keep this page open and retry.');

  function restore() {
    if (restored) return restored;
    restored = (async () => {
      if (!session.snapshot) return null;
      const remote = await send({ action: 'load' });
      lastResult = remote;
      if (remote) {
        session.attachments = remote.data.attachments || [];
        if (session.pending?.saveId === remote.data.progress.saveId) {
          session.dirty = JSON.stringify(session.snapshot) !== JSON.stringify(session.pending.payload);
          session.pending = null;
        } else if (remote.revision !== session.revision) {
          // Explicit reload after a conflict resumes the server's newer copy.
          session.snapshot = snapshotOf(remote);
          session.pending = null;
          session.dirty = false;
        }
        session.revision = remote.revision;
        session.submitted = remote.data.progress.status === 'submitted';
        if (session.submitted) {
          session.snapshot = snapshotOf(remote);
          session.pending = null;
          session.dirty = false;
        }
      } else if (session.revision > 0) {
        const error = new Error('This draft is unavailable. Please contact us for help.');
        error.status = 403;
        throw error;
      }
      persist();
      onStatus(session.submitted ? 'submitted' : session.dirty ? 'saving' : 'saved');
      return remote;
    })().catch((error) => { notifyError(error); throw error; });
    return restored;
  }

  async function flush(action = 'save', options) {
    clearTimeout(timer);
    if (inFlight) {
      await inFlight;
      return flush(action, options);
    }
    if (session.submitted || !session.snapshot || (!session.dirty && !session.pending && action === 'save')) return lastResult;
    if (!session.pending) {
      session.pending = { action, revision: session.revision + 1, saveId: crypto.randomUUID(), payload: session.snapshot };
      persist();
    }
    const operation = session.pending;
    const sentAttachmentVersion = attachmentVersion;
    onStatus('saving');
    inFlight = send(operation, options).then((remote) => {
      lastResult = remote;
      session.revision = remote.revision;
      if (sentAttachmentVersion === attachmentVersion) session.attachments = remote.data.attachments || [];
      session.submitted = remote.data.progress.status === 'submitted';
      session.dirty = !session.submitted && JSON.stringify(session.snapshot) !== JSON.stringify(operation.payload);
      session.pending = null;
      persist();
      onStatus(session.submitted ? 'submitted' : session.dirty ? 'saving' : 'saved');
      return remote;
    }).catch((error) => { notifyError(error); throw error; });
    let result;
    try { result = await inFlight; } finally { inFlight = null; }
    if (action === 'submit' && !session.submitted) return flush('submit', options);
    if (session.dirty) timer = setTimeout(() => flush().catch(() => {}), delay);
    return result;
  }

  return {
    restore,
    flush,
    getSnapshot: () => session.snapshot,
    isSubmitted: () => !!session.submitted,
    canResume: () => resumable,
    getAttachments: () => session.attachments || [],
    async attachments(action, details = {}) {
      // Once the draft exists, attachment recovery must also work while a
      // submit is waiting for unfinished uploads to be completed or removed.
      if (!session.revision) await flush();
      if (!session.revision) throw new Error('Enter your contact information before attaching files.');
      const result = await attachmentRequest({ ...details, action, draftId: session.id, draftToken: session.token });
      attachmentVersion++;
      session.attachments = result.attachments;
      persist();
      return result;
    },
    update(snapshot, immediate = false) {
      if (session.submitted || JSON.stringify(snapshot) === JSON.stringify(session.snapshot)) return;
      session.snapshot = snapshot;
      session.dirty = true;
      persist();
      onStatus('saving');
      clearTimeout(timer);
      timer = setTimeout(() => flush().catch(() => {}), immediate ? 0 : delay);
    },
    cancelTimer() { clearTimeout(timer); },
    startNew() { storage?.removeItem(DRAFT_STORAGE_KEY); },
  };
}
